// ----------------------------------------------------------------------------
// -------------------------------------------------------------------- Imports
// ----------------------------------------------------------------------------
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Libraries
import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { css } from "glamor";
import moment from "moment";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Components
import Link from "gatsby-link";
import {
  Page,
  // Section,
  Article,
  Header,
  Footer,
} from "@bodhi-project/semantic-webflow";
import { Elements } from "@bodhi-project/typography";
import { treeCodeParser, tocParser } from "@bodhi-project/markdown-to-react";
import {
  // --------------- Basic
  UpdateTitle,
  GeneralMeta,
  // --------------- Twitter
  TwitterSummaryCard,
  // --------------- Open Graph
  OpenGraphSummary,
  // --------------- Schema.org JSON-LD
  WebpageSchema,
  BreadcrumbSchema,
} from "@bodhi-project/seo";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Locals
import seoHelper from "../helpers/seoHelper";
import packageJson from "../../package.json";
import markdownStylesClass from "../styles/markdownStyles";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Abstractions
const { Fragment } = React;
const { data } = packageJson;
const { H1, Paragraph } = Elements;

// ----------------------------------------------------------------------------
// --------------------------------------------------------------------- Styles
// ----------------------------------------------------------------------------
const pageStyle = css({
  "@media(max-width: 768px)": {
    flexDirection: "column",

    "& .doc-tree": {
      order: 2,
      margin: "0em 1em",
    },

    "& .doc-content": {
      order: 1,
      borderBottom: "1px solid #d1b7a1",
      margin: "2em 1em 1em 1em",
    },

    paddingTop: 0,
  },

  paddingTop: 72,
  backgroundImage: "linear-gradient(135deg, #fdfcfb 0%, #e2d1c3 100%)",
  display: "flex",
  minHeight: "100vh",

  "@media(min-width: 768px)": {
    paddingTop: "10vh",
    paddingBottom: "10vh",
  },

  "& .md-cat": {
    color: "rgba(0,0,12,0.625)",
    marginBottom: 10,
  },

  "& li.md-page-active": {
    height: "auto",
    transition: "all 100ms cubic-bezier(0.215, 0.61, 0.355, 1)",

    "& span.active-dot": {
      display: "none",
    },

    "& > a": {
      color: "#F15D58",
      borderBottom: "1.625px solid transparent",
    },
  },
  "& .doc-tree": {
    "& span": {
      fontSize: "90%",
    },
  },

  "& .md-pages": {
    marginTop: "0px !important",

    "& a": {
      color: "rgba(0,0,12,0.625)",
    },

    "& li": {
      marginBottom: 5,

      "&:before": {
        borderTop: "1.375px solid #858A97",
        borderLeft: "1.375px solid #858A97",
      },

      "&:after": {
        borderLeft: "1.375px solid #858A97",
        bottom: "0.62em",
      },

      "&:first-child": {
        marginTop: "15px !important",
      },

      "&:last-child": {
        "&:before": {
          borderLeft: "0px !important",
        },
      },
    },
  },

  "& .md-headings": {
    "& li": {
      "&:before": {
        borderTop: "1.375px solid #858A97",
        borderLeft: "1.375px solid #858A97",
      },

      "&:after": {
        borderLeft: "1.375px solid #858A97",
        bottom: "0.62em",
      },
    },
  },
});
const pageStyleClass = pageStyle.toString();

// ----------------------------------------------------------------------------
// ------------------------------------------------------------------ Component
// ----------------------------------------------------------------------------
/** DocTemplate */
class DocTemplate extends React.Component {
  /** standard constructor */
  constructor(props) {
    super(props);
  }

  /** standard renderer */
  render() {
    // Abstract stuff
    const { pathContext } = this.props;
    const { frontmatter } = pathContext;
    const { toc } = pathContext;
    const { markdownAst } = pathContext;
    const { route } = pathContext;
    const checkedRoute = _.startsWith(route, "/") ? route : `/${route}`;
    const nakedRoute = checkedRoute.substr(1);
    const { headings } = pathContext;
    const onlyDocs = [];
    _.map(toc, ({ node }) => {
      if (_.startsWith(_.trim(node.fields.route), "docs") === true) {
        onlyDocs.push({ node });
      }
    });

    // Date stuff
    const mDate = moment(frontmatter.date);
    const humanDate = mDate.format("dddd, MMMM Do YYYY");
    const elapsed = mDate.fromNow();

    const pageData = {
      pageTitle: frontmatter.title,
      nakedPageSlug: nakedRoute,
      pageAbstract: frontmatter.abstract,
    };

    const seoData = seoHelper(pageData);

    const {
      pageTitle,
      generalMetaData,
      twitterSummaryCardData,
      openGraphSummaryData,
      webpageSchemaData,
      breadcrumbSchemaData,
    } = seoData;

    return (
      <Fragment>
        {/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ SEO */}
        <UpdateTitle title={pageTitle} />
        <GeneralMeta data={generalMetaData} />
        <TwitterSummaryCard data={twitterSummaryCardData} />
        <OpenGraphSummary data={openGraphSummaryData} />
        <WebpageSchema data={webpageSchemaData} />
        <BreadcrumbSchema data={breadcrumbSchemaData} />

        {/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Content */}
        <Page className={`${markdownStylesClass} ${pageStyleClass}`}>
          <span className="is-hidden-mobile" style={{ flexGrow: 9 }} />
          <div
            className="doc-tree"
            style={{
              flexGrow: 20,
              flexBasis: 0,
            }}
          >
            {tocParser(
              onlyDocs,
              this.props.location.pathname,
              { localLink: Link },
              {},
              { where: frontmatter.title, what: headings },
            )}
          </div>
          <span className="is-hidden-mobile" style={{ flexGrow: 2 }} />
          <div
            className="doc-content"
            style={{
              flexGrow: 40,
              flexBasis: 0,
            }}
          >
            <Article>
              <Header className="stash">
                <H1>{frontmatter.title}</H1>
                <Paragraph>{humanDate}</Paragraph>
                <Paragraph className="stash">{frontmatter.abstract}</Paragraph>
              </Header>
              {treeCodeParser(
                markdownAst,
                {
                  localLink: Link,
                  linkHeaders: true,
                  trackHeaders: false,
                  nestHeaders: true,
                },
                {},
              )}
              <Footer className="stash">
                <Paragraph>
                  Copyright&nbsp;{data.websiteCreator}&nbsp;2018.
                  <br />
                  <br />
                  Published on {humanDate} ({elapsed}).
                </Paragraph>
              </Footer>
            </Article>
          </div>
          <span className="is-hidden-mobile" style={{ flexGrow: 9 }} />
        </Page>
      </Fragment>
    );
  }
}

DocTemplate.propTypes = {
  pathContext: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

// ----------------------------------------------------------------------------
// --------------------------------------------------------------------- Export
// ----------------------------------------------------------------------------
export default DocTemplate;



// WEBPACK FOOTER //
// ./src/templates/doc.jsx